import { useState } from "react";
import { yerelHaftaBasi } from "@koclukpanel/contracts";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  DetayliAnaliz,
  FiltreKarti,
  Iskelet,
  Rozet,
  Secim,
  Tablo,
  Kart,
  SekmeHapi,
} from "@koclukpanel/ui";
import {
  ApiHatasi,
  detayliAnaliz,
  detayliAnalizPdf,
  haftalikAnaliz,
  type Degisim,
  type HaftalikAnaliz as Analiz,
} from "./api";
import { SayfaBasligiEki } from "./Kabuk";

/**
 * HAFTALIK ÖĞRENCİ ANALİZİ — F-ANL-02/03/04/05/08
 *
 * ================================================================
 * BU EKRAN 04.09.2026'YA KADAR HİÇ YOKTU
 * ================================================================
 * `GET /analiz/ogrenci/:id/hafta` sunucuda yazılmıştı ve dört
 * başlıklı bir analiz üretiyordu — program uyumu, akademik
 * performans, çalışma davranışı, müdahale önerileri. Hiçbir ekran
 * çağırmıyordu, yani hesaplanan her şey görünmezdi.
 *
 * ================================================================
 * ÖLÇÜLEMEYEN İLE SIFIR AYNI GÖSTERİLMİYOR
 * ================================================================
 * Sunucu ölçülemeyen metriği `null` döndürüyor, 0 değil. Ekran da
 * ikisini ayırıyor: "%0 başarı" ile "başarı ölçülemedi" bambaşka
 * iki durum ve ikincisinde suçlanacak kimse yok.
 *
 * ================================================================
 * BU EKRANIN TUVALİ YOK — ÖLÇÜLDÜ
 * ================================================================
 * `HaftalikProgram.dc.html` bu ekrana ait sanılabilir; değil.
 * İçeriği ölçüldü: 7 gün × saatli görev ızgarası ("16:00 · 60dk ·
 * Matematik · Türev · 40 soru") — bir program DÜZENLEYİCİ.
 * `docs/tasarim/tuval/` içinde "program uyumu", "çalışma davranışı"
 * veya "müdahale önerisi" geçen tek dosya `VeliPortaliMasaustu`,
 * o da veli görünümü.
 *
 * Yani burada "tuvale sadık kal" diye bakılacak bir referans YOK;
 * yanlış tuvale giydirmek, olmayan bir sözleşmeye uymak olurdu.
 * Ekran `packages/ui` bileşenleri ve belirteçlerle kuruluyor.
 *
 * ================================================================
 * ÖNERİLER ŞABLON TABANLI — YAPAY ZEKÂ YOK
 * ================================================================
 * Aynı girdi her zaman aynı çıktıyı veriyor ve cümlenin hangi
 * ölçümden geldiği açıklanabiliyor. Ekran bunu yazıyor: uzman
 * öneriyi öğrenciye okurken arkasında ne olduğunu bilmeli.
 */

const YORUM_TONU: Record<Degisim["yorum"], "notr" | "bilgi" | "onay" | "uyari" | "hata"> = {
  IYILESME: "onay",
  KOTULESME: "hata",
  AYNI: "notr",
  KARSILASTIRILAMAZ: "notr",
};

const YORUM_ADI: Record<Degisim["yorum"], string> = {
  IYILESME: "iyileşme",
  KOTULESME: "düşüş",
  AYNI: "değişmedi",
  // "karşılaştırılamaz" tek başına neyin eksik olduğunu söylemiyordu.
  KARSILASTIRILAMAZ: "geçen hafta ölçülemedi",
};

const ONCELIK_TONU: Record<Analiz["oneriler"][number]["oncelik"], "hata" | "uyari" | "bilgi"> = {
  YUKSEK: "hata",
  ORTA: "uyari",
  BILGI: "bilgi",
};

const ONCELIK_ADI: Record<Analiz["oneriler"][number]["oncelik"], string> = {
  YUKSEK: "Yüksek",
  ORTA: "Orta",
  BILGI: "Bilgi",
};

/** `null` = ÖLÇÜLEMEDİ. "%0" yazmak ölçülmemiş bir şeyi sıfır göstermek olurdu. */
function oran(x: number | null): string {
  return x === null ? "—" : `%${Math.round(x * 100)}`;
}

function sure(saniye: number): string {
  if (saniye <= 0) return "—";
  const s = Math.round(saniye / 60);
  if (s < 60) return `${s} dk`;
  return `${Math.floor(s / 60)} sa ${s % 60} dk`;
}

/**
 * ================================================================
 * YÖN TEK BAŞINA YETMİYOR — BÜYÜKLÜK DE GEREKİYOR
 * ================================================================
 * Sunucu `fark` ve `oransalDegisim`i hesaplayıp gönderiyordu; ekran
 * yalnızca "iyileşme" yazıp ikisini de atıyordu. Sonuç: 1 puanlık
 * oynama ile 30 puanlık çöküş EKRANDA BİRBİRİNİN AYNI görünüyordu ve
 * uzman hangisine müdahale edeceğini rozetten okuyamıyordu.
 *
 * Biçim metriğe göre değişmek zorunda: oranlarda fark YÜZDE PUAN
 * (0.72 − 0.60 = 12 puan), çalışma süresinde ise SANİYE. Tek bir
 * ortak biçimlendirici, "+7200 puan" gibi bir cümle üretirdi.
 */
function puanFarki(fark: number): string {
  return `${fark > 0 ? "+" : "−"}${Math.round(Math.abs(fark) * 100)} puan`;
}

function sureFarki(fark: number): string {
  const mutlak = Math.abs(fark);
  const isaret = fark > 0 ? "+" : "−";
  // 60 sn altını `sure` "0 dk" diye yazardı: gerçek bir farkı yokmuş
  // gibi göstermek olurdu.
  if (mutlak < 60) return `${isaret}${Math.round(mutlak)} sn`;
  return `${isaret}${sure(mutlak)}`;
}

function tarih(iso: string): string {
  return new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "long" });
}

/**
 * Hafta başını ISO gün olarak verir (pazartesi).
 *
 * `yerelHaftaBasi` — eski hâli `getUTCDate()` kullanıyordu ve Pazartesi
 * yerel 00:00–02:59 arasında **bir hafta geri** atıyordu (UTC hâlâ
 * Pazar, ISO hesabı Pazar'ı önceki haftaya bağlıyor). O saatte açılan
 * analiz geçen haftanınkiydi ve hiçbir şey bozuk görünmüyordu.
 */
function haftaBasi(kaydirma: number): string {
  const g = yerelHaftaBasi(new Date());
  // Kaydirma HAFTA BASINDAN sonra uygulaniyor: once dogru haftayi bul,
  // sonra kaydir. Ters sirada, kaydirilmis bir anin hafta basi
  // hesaplanirdi ve yaz saati gecislerinde bir gun oynardi.
  g.setUTCDate(g.getUTCDate() + kaydirma * 7);
  return g.toISOString().slice(0, 10);
}

/** Hafta başı (ISO gün) + 6 gün. UTC aritmetiği: ISO gün zaten saat diliminden bağımsız. */
function haftaSonuMetni(bas: string): string {
  const g = new Date(`${bas}T00:00:00Z`);
  g.setUTCDate(g.getUTCDate() + 6);
  return g.toISOString().slice(0, 10);
}

/**
 * ================================================================
 * SERBEST İKİ HAFTA — SÜRÜM 9a (UNİKOÇ Gelişim Takip "haftaları değiştir")
 * ================================================================
 * Eskiden karşılaştırılan hafta "1–8 hafta önce" açılır listesiydi. Şimdi iki hafta da tarih
 * seçiciyle serbest: seçilen günün haftası alınır. Sınır SUNUCUDA (en çok 104 hafta geriye,
 * gelecek yok, iki hafta aynı olamaz); buradaki min/max yalnız seçiciyi o aralığa daraltır,
 * kuralı kopyalamaz — yazılan/yapıştırılan değer yine `haftaFarki`nda kenetlenir.
 */
const GERIYE_HAFTA_SINIRI = 104;
const GUN_MS = 24 * 60 * 60 * 1000;

/** ISO günün (YYYY-AA-GG) haftasının pazartesisi — UTC aritmetiği, saat diliminden bağımsız. */
function pazartesi(isoGun: string): Date | null {
  const d = new Date(`${isoGun}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d;
}

/** Seçilen günün haftası, bu haftaya göre kaç hafta kaymış; sınıra kenetli (≤ 0, ≥ −sınır). */
function haftaFarki(isoGun: string): number | null {
  const p = pazartesi(isoGun);
  if (!p) return null;
  const bu = new Date(`${haftaBasi(0)}T00:00:00Z`);
  const fark = Math.round((p.getTime() - bu.getTime()) / (7 * GUN_MS));
  return Math.max(-GERIYE_HAFTA_SINIRI, Math.min(0, fark));
}

/** base64 PDF'i tarayıcıda dosya olarak indirir. */
function pdfIndir(dosyaAdi: string, base64: string): void {
  const bayt = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const adres = URL.createObjectURL(new Blob([bayt], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = adres;
  a.download = dosyaAdi;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(adres);
}

export function HaftalikAnaliz({
  ogrenciId,
  ogrenciAdi,
  onGeri,
}: {
  ogrenciId: string;
  ogrenciAdi: string;
  onGeri?: () => void;
}) {
  const [kaydirma, setKaydirma] = useState(0);
  const hafta = haftaBasi(kaydirma);
  /*
    ================================================================
    ÖZET + DETAY — 04.10.2026 (müşteri revizesi madde 2)
    ================================================================
    Müşteri "gün gün ve hafta hafta detaylı analiz" istedi. Özet
    (program uyumu, öneriler) koçun haftalık görüşmedeki ilk bakışı
    olarak kalıyor; detay ayrı bir görünüm. İkisi aynı hafta seçiciyi
    paylaşıyor — ayrı ekranlar olsaydı koç iki yerde aynı haftayı
    aramak zorunda kalırdı.

    Karşılaştırma haftası varsayılan olarak bir önceki hafta; referans
    ekranda iki hafta da seçilebiliyordu, burada geriye 8 haftaya kadar.
  */
  const [gorunum, setGorunum] = useState<"ozet" | "detay">("ozet");
  /** Karşılaştırılan hafta: bu haftaya göre kayma (≤ 0); `null` = analiz haftasının bir öncesi. */
  const [karsiKaydirma, setKarsiKaydirma] = useState<number | null>(null);
  const efektifKarsi = karsiKaydirma ?? kaydirma - 1;
  const karsiHafta = haftaBasi(efektifKarsi);
  const ayniHafta = karsiHafta === hafta;
  const sinirDisi = efektifKarsi < -GERIYE_HAFTA_SINIRI;
  const [suzgecDers, setSuzgecDers] = useState<string>("");
  const [suzgecKonu, setSuzgecKonu] = useState<string>("");
  const suzgec = {
    ders: suzgecDers || undefined,
    konu: suzgecKonu || undefined,
  };

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["haftalik-analiz", ogrenciId, hafta],
    queryFn: () => haftalikAnaliz(ogrenciId, hafta),
    retry: false,
    enabled: gorunum === "ozet",
  });

  const detay = useQuery({
    queryKey: ["detayli-analiz", ogrenciId, hafta, karsiHafta, suzgecDers, suzgecKonu],
    queryFn: () => detayliAnaliz(ogrenciId, hafta, karsiHafta, suzgec),
    retry: false,
    enabled: gorunum === "detay" && !ayniHafta && !sinirDisi,
    // Süzgeç değişince açılır listeler (seçenekler yanıttan geliyor) boşalıp seçimi kaybettirmesin.
    placeholderData: (onceki) => onceki,
  });

  const pdf = useMutation({
    mutationFn: () => detayliAnalizPdf(ogrenciId, hafta, karsiHafta, suzgec),
    onSuccess: (s) => pdfIndir(s.dosyaAdi, s.pdfBase64),
  });
  const secenekler = detay.data?.suzgecSecenekleri?.dersler ?? [];
  const seciliDers = secenekler.find((d) => d.dersId === suzgecDers);

  return (
    <div className="flex flex-col gap-6">
      {/*
        UNİKOÇ ANALİZ VE RAPORLAMA + GELİŞİM TAKİP (sürüm 9a B2; tarama-2 §3.4, tarama-1 §3.16).
        Başlık bandının açıklaması görünüme göre değişiyor: haftalık özet = "Analiz ve Raporlama"
        cümlesi, detaylı analiz (iki hafta karşılaştırması) = "Gelişim Takip" cümlesi. Eski sayfada
        bunlar iki ayrı menü girdisiydi; bizde tek ekranın iki görünümü ve ikisi AYNI hafta seçiciyi
        paylaşıyor (aşağıdaki gerekçe). "İş listesi" dönüşü bandın sağında kesikli hayalet düğme.
      */}
      <SayfaBasligiEki
        aciklama={
          gorunum === "ozet"
            ? "Günlük faaliyetleri kart görün, haftalık toplamları analiz edin."
            : "Hafta–hafta karşılaştır, gelişimi ders/konu bazında takip et."
        }
        eylemler={
          onGeri ? (
            <Buton gorunum="kesikli" onClick={onGeri}>
              ← İş listesi
            </Buton>
          ) : undefined
        }
      />

      <div role="group" aria-label="Analiz görünümü" className="flex flex-wrap gap-2">
        <SekmeHapi secili={gorunum === "ozet"} onClick={() => setGorunum("ozet")}>
          Haftalık özet
        </SekmeHapi>
        <SekmeHapi secili={gorunum === "detay"} onClick={() => setGorunum("detay")}>
          Detaylı analiz
        </SekmeHapi>
      </div>

      <FiltreKarti baslik="Filtreler" ikon="program">
        <div className="flex flex-col gap-4">
          {onGeri && (
            <p className="text-metin-ikincil text-sm">
              Öğrenci: <span className="text-metin-koyu font-semibold">{ogrenciAdi}</span>
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Buton
              gorunum="ikincil"
              disabled={kaydirma <= -(GERIYE_HAFTA_SINIRI - 1)}
              onClick={() => setKaydirma(kaydirma - 1)}
            >
              ← Önceki hafta
            </Buton>
            {/* Güncel hafta rozeti: UNİKOÇ'ta mor hap (tarama-1 §2.1 "Güncel hafta rozeti"). */}
            <span className="bg-mor-50 text-mor-700 rounded-rozet px-3 py-1.5 text-sm font-semibold">
              {tarih(hafta)} – {tarih(haftaSonuMetni(hafta))}
            </span>
            <Buton
              gorunum="ikincil"
              disabled={kaydirma >= 0}
              onClick={() => setKaydirma(kaydirma + 1)}
            >
              Sonraki hafta →
            </Buton>
            {kaydirma !== 0 && (
              <Buton gorunum="kesikli" onClick={() => setKaydirma(0)}>
                Bu hafta
              </Buton>
            )}
            {/* Serbest hafta: seçilen günün haftası (sunucu 104 haftadan eskisini ve geleceği reddeder). */}
            <Alan
              etiket="Haftayı tarihle seç"
              etiketGizli
              type="date"
              value={hafta}
              max={haftaBasi(0)}
              min={haftaBasi(-GERIYE_HAFTA_SINIRI)}
              onChange={(e) => {
                const f = haftaFarki(e.target.value);
                if (f !== null) setKaydirma(f);
              }}
            />
          </div>
        </div>
      </FiltreKarti>

      {gorunum === "detay" && (
        <>
          {/*
            UNİKOÇ Gelişim Takip (sürüm 9a): iki hafta serbest, ders/konu süzgeci, PDF.
            Karşılaştırılan hafta tarih seçici; boşsa "analiz haftasının bir öncesi".
          */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex flex-col gap-1">
              <Alan
                etiket="Karşılaştırılan hafta"
                type="date"
                value={karsiHafta}
                max={haftaBasi(0)}
                min={haftaBasi(-GERIYE_HAFTA_SINIRI)}
                hata={
                  ayniHafta
                    ? "Karşılaştırılan hafta, analiz edilen haftayla aynı olamaz."
                    : sinirDisi
                      ? `En çok ${GERIYE_HAFTA_SINIRI} hafta geriye gidilebilir.`
                      : undefined
                }
                onChange={(e) => {
                  const f = haftaFarki(e.target.value);
                  if (f !== null) setKarsiKaydirma(f);
                }}
              />
              {karsiKaydirma !== null && (
                <Buton gorunum="kesikli" onClick={() => setKarsiKaydirma(null)}>
                  Bir önceki haftaya dön
                </Buton>
              )}
            </div>
            <Secim
              etiket="Ders"
              value={suzgecDers}
              onChange={(e) => {
                setSuzgecDers(e.target.value);
                // Konu seçili dersin içindeydi; ders değişince eski konu anlamsız (ve çelişkili istek).
                setSuzgecKonu("");
              }}
            >
              <option value="">Tüm dersler</option>
              {secenekler.map((d) => (
                <option key={d.dersId} value={d.dersId}>
                  {d.ders}
                </option>
              ))}
            </Secim>
            <Secim
              etiket="Konu"
              value={suzgecKonu}
              disabled={!seciliDers}
              onChange={(e) => setSuzgecKonu(e.target.value)}
            >
              <option value="">{seciliDers ? "Tüm konular" : "Önce ders seçin"}</option>
              {(seciliDers?.konular ?? []).map((k) => (
                <option key={k.konuId} value={k.konuId}>
                  {k.konu}
                </option>
              ))}
            </Secim>
            <div className="flex items-end">
              <Buton
                gorunum="ikincil"
                yukleniyor={pdf.isPending}
                disabled={ayniHafta || sinirDisi || !detay.data}
                onClick={() => pdf.mutate()}
              >
                PDF indir
              </Buton>
            </div>
          </div>
          {pdf.isError && (
            <p role="alert" className="text-hata-700 text-sm">
              {pdf.error instanceof ApiHatasi ? pdf.error.message : "PDF oluşturulamadı."}
            </p>
          )}
          {detay.isPending && !ayniHafta && !sinirDisi && <Iskelet satir={6} />}
          {detay.isError && (
            <BosDurum
              ton="hata"
              baslik="Detaylı analiz alınamadı"
              aciklama={detay.error instanceof ApiHatasi ? detay.error.message : undefined}
              eylem={
                <Buton gorunum="ikincil" onClick={() => void detay.refetch()}>
                  Tekrar dene
                </Buton>
              }
            />
          )}
          {detay.data && <DetayliAnaliz veri={detay.data} />}
        </>
      )}

      {gorunum === "ozet" && isPending && <Iskelet satir={6} />}

      {gorunum === "ozet" && isError && (
        <BosDurum
          ton="hata"
          baslik="Analiz alınamadı"
          aciklama={error instanceof ApiHatasi ? error.message : undefined}
          eylem={
            <Buton gorunum="ikincil" onClick={() => void refetch()}>
              Tekrar dene
            </Buton>
          }
        />
      )}

      {gorunum === "ozet" && data && (
        <>
          {/*
            ================================================================
            KAPSAM UYARISI EN ÜSTTE — ÖLÇÜMLERDEN ÖNCE
            ================================================================
            Veri kapsamı düşükse aşağıdaki her sayı yanıltıcı: on iki
            görevin ikisine veri girilmişse "%50 başarı" iki göreve ait
            bir sayıdır. Uyarıyı sayıların ALTINA koymak, sayıları okuyup
            uyarıyı okumamak demekti.

            Sunucu cümleyi kendisi yazıyor; ekran uydurmuyor.
          */}
          {data.kapsam.dusukMu && (
            <p
              role="alert"
              className="rounded-alan bg-uyari-500/15 text-uyari-800 px-3 py-2 text-sm"
            >
              {data.kapsam.metin}
            </p>
          )}

          {data.kapsam.toplamGorev === 0 ? (
            <BosDurum baslik="Bu hafta için veri yok" aciklama={data.kapsam.metin} />
          ) : (
            <>
              <Kart gorunum="unk" baslik="Program uyumu" ikon="plan">
                <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <Olcum etiket="Atanan görev" deger={String(data.programUyumu.atanan)} />
                  <Olcum etiket="Tamamlanan" deger={String(data.programUyumu.tamamlanan)} />
                  <Olcum etiket="Mazeretli" deger={String(data.programUyumu.mazeretli)} />
                  {/*
                    Payda ATANAN DEĞİL: 20 atanan / 3 mazeretli / 12
                    tamamlanan satırını yan yana gören uzman kafasında
                    12÷20 = %60 hesaplıyor, ekranda %71 görüyor ve
                    ekranı hatalı sanıyor. Formülü yazmak, üç sayının
                    yanına dördüncüyü koymanın bedeli.
                  */}
                  <Olcum
                    etiket="Tamamlama oranı"
                    deger={oran(data.programUyumu.tamamlamaOrani)}
                    degisim={data.oncekiHaftayaGore.tamamlamaOrani}
                    farkBicimi={puanFarki}
                    aciklama="Tamamlanan / (Atanan − Mazeretli)"
                  />
                  <Olcum etiket="Erteleme oranı" deger={oran(data.programUyumu.ertelemeOrani)} />
                  <Olcum
                    etiket="Devamlılık"
                    deger={oran(data.programUyumu.devamlilik)}
                    aciklama="Veri girilen gün / geçen gün"
                  />
                </dl>
              </Kart>

              <Kart gorunum="unk" baslik="Akademik performans" ikon="analiz">
                <div className="flex flex-col gap-4">
                  <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <Olcum etiket="Doğru" deger={String(data.akademik.dogru)} />
                    <Olcum etiket="Yanlış" deger={String(data.akademik.yanlis)} />
                    <Olcum etiket="Boş" deger={String(data.akademik.bos)} />
                    {/*
                      Boş bırakılanlar PAYDAYA GİRMİYOR (sunucudaki
                      gerekçe: boş bırakmak "bilmediğini bilmek"tir).
                      Ama Doğru/Yanlış/Boş üçlüsünün hemen yanında
                      duran bir yüzde, okuyanı Doğru÷Toplam hesabına
                      davet ediyor — 40/10/50 satırında beklenen %40,
                      görünen %80.
                    */}
                    <Olcum
                      etiket="Başarı oranı"
                      deger={oran(data.akademik.basariOrani)}
                      degisim={data.oncekiHaftayaGore.basariOrani}
                      farkBicimi={puanFarki}
                      aciklama="Doğru / (Doğru + Yanlış) — boşlar hariç"
                    />
                  </dl>

                  {data.akademik.dersler.length > 0 && (
                    <Tablo
                      satirlar={data.akademik.dersler}
                      anahtar={(d) => d.ders}
                      kartBasligi={0}
                      sutunlar={[
                        {
                          baslik: "Ders",
                          mobil: true,
                          hucre: (d) => d.ders,
                          className: "font-medium",
                        },
                        { baslik: "Doğru", mobil: true, sayisal: true, hucre: (d) => d.dogru },
                        { baslik: "Yanlış", mobil: true, sayisal: true, hucre: (d) => d.yanlis },
                        {
                          baslik: "Başarı",
                          mobil: true,
                          sayisal: true,
                          hucre: (d) => oran(d.basariOrani),
                        },
                      ]}
                    />
                  )}
                </div>
              </Kart>

              <Kart gorunum="unk" baslik="Çalışma davranışı" ikon="bekliyor">
                <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {/*
                    ================================================
                    TOPLAM YANLIŞ DEĞİL — EKSİK. VE EKSİKLİĞİ GÖRÜNMÜYORDU
                    ================================================
                    Öğrenci sayacı çalıştırmadan yalnızca cevapları
                    girerek görevi tamamlayabiliyor; o görev toplama
                    SIFIR katkı yapıyor. Ekran "4 sa 30 dk" yazıyordu,
                    okuyan bunu TAM sanıyordu.

                    `?? 0` doğru: bir toplamda süresiz görev sıfır
                    katkı yapmalı. Eksik olan, toplamın eksik olduğunu
                    SÖYLEYEN sayıydı — sunucu artık `sureGirilmeyenGorev`
                    ile onu da gönderiyor.
                  */}
                  <Olcum
                    etiket="Toplam süre"
                    deger={sure(data.calismaDavranisi.toplamSureSaniye)}
                    degisim={data.oncekiHaftayaGore.calismaSuresi}
                    farkBicimi={sureFarki}
                    aciklama={
                      data.calismaDavranisi.sureGirilmeyenGorev > 0
                        ? `${data.calismaDavranisi.sureGirilmeyenGorev} görevde süre girilmemiş — toplam eksik`
                        : undefined
                    }
                  />
                  {/*
                    ================================================
                    EKSİK TOPLAM, ORTALAMAYI DA BOZUYOR
                    ================================================
                    Ortalama = toplam süre / TAMAMLANAN görev. Payda
                    doğru, ama PAY eksik: süresi girilmemiş görevlerin
                    süresi toplamda yok, buna karşılık görevin kendisi
                    paydada sayılıyor olabilir. Yani ortalama gerçekte
                    olduğundan DÜŞÜK çıkıyor.

                    Ne kadar düşük çıktığı istemciden hesaplanamaz
                    (eksik sürelerin kaçının tamamlanmış göreve ait
                    olduğu bilinmiyor). Uydurmak yerine yönü söylüyorum:
                    eksik süreler eklenince pay ancak BÜYÜR, payda
                    değişmez — dolayısıyla bu sayı bir ALT SINIR.
                    Kanıtlanabilir olan bu; "yaklaşık şu kadar" değil.
                  */}
                  <Olcum
                    etiket="Görev başına"
                    deger={
                      data.calismaDavranisi.ortalamaGorevSuresi === null
                        ? "—"
                        : sure(data.calismaDavranisi.ortalamaGorevSuresi)
                    }
                    aciklama={
                      data.calismaDavranisi.sureGirilmeyenGorev > 0 &&
                      data.calismaDavranisi.ortalamaGorevSuresi !== null
                        ? "Aynı eksik burada da var — gerçek ortalama en az bu kadar"
                        : undefined
                    }
                  />
                  {/*
                    ================================================
                    "VERİMLİ" YANLIŞ SÖZCÜKTÜ
                    ================================================
                    Ölçüldü — sunucu bu günü `correctCount + wrongCount`
                    toplamına göre seçiyor, yani SADECE HACİM. Verim
                    ise çıktı/emek oranıdır. 6 saatte 100 soruyu %20
                    doğrulukla çözen gün, 40 soruyu %90 doğrulukla
                    çözen günü bu sıralamada yeniyor ve ekran ona
                    "en verimli" diyordu.
                    Etiketi ölçümün kendisiyle değiştirdim: sözcük
                    doğruysa açıklamaya gerek kalmıyor.
                  */}
                  <Olcum
                    etiket="En çok soru çözülen gün"
                    deger={data.calismaDavranisi.enVerimliGun ?? "—"}
                    aciklama="Doğru + yanlış toplamı — doğruluk hesaba girmez"
                  />
                </dl>
              </Kart>

              <Kart
                gorunum="unk"
                baslik="Müdahale önerileri"
                ikon="hedef"
                eylem="Şablon tabanlı: aynı ölçüm her zaman aynı öneriyi üretir. Yapay zekâ kullanılmıyor."
              >
                {data.oneriler.length === 0 ? (
                  /*
                    ================================================
                    "ÖNERİ YOK" İLE "KURAL HİÇ İŞLEMEDİ" AYNI DEĞİL
                    ================================================
                    Önceki cümle koşulsuzdu: "ölçümler eşiklerin
                    dışına çıkmadı." Bu bir GÜVENCE cümlesi — uzman
                    okuduğunda "baktık, sorun yok" anlıyor.

                    Ölçüldü (analiz-metrik.ts, `mudahaleOnerileri`):
                    doğruluk kurallarının ikisi de
                    `oncekiBasari !== null && simdikiBasari !== null`
                    istiyor. Önceki haftada hiç soru çözülmemişse
                    `oncekiBasari` null ve O KURALLAR HİÇ ÇALIŞMIYOR.
                    Ekran yine de "eşiklerin dışına çıkmadı" diyordu:
                    ölçülemeyeni ölçülmüş ve temiz çıkmış gibi
                    gösteriyordu — bu deponun kendi kuralının
                    (ölçülmedi ≠ sıfır) tam ihlali.

                    Sistem boş açılıyor ve her öğrencinin İLK haftası
                    bu durumda. Yani yanlış cümlenin görüldüğü an,
                    istisna değil kural.

                    Haftalık toplam başarı `KARSILASTIRILAMAZ` ise
                    hiçbir dersin önceki verisi yoktur — yeterli
                    koşul, o yüzden bu ayrım güvenli tarafta.
                  */
                  data.oncekiHaftayaGore.basariOrani.yorum === "KARSILASTIRILAMAZ" ? (
                    <p className="text-notr-600 text-sm">
                      Öneri üretilmedi — ancak bu{" "}
                      <strong className="font-medium">“sorun yok”</strong> demek değil. Önerilerin
                      bir bölümü önceki haftayla karşılaştırma gerektiriyor; geçen hafta
                      ölçülemediği için o kurallar hiç çalışmadı.
                    </p>
                  ) : (
                    <p className="text-notr-600 text-sm">
                      Bu hafta için öneri üretilmedi: ölçülebilen değerler eşiklerin dışına çıkmadı.
                      Önceki haftada verisi olmayan dersler karşılaştırmaya girmez.
                    </p>
                  )
                ) : (
                  <ul className="flex flex-col gap-2">
                    {data.oneriler.map((o, sira) => (
                      <li
                        key={o.anahtar ?? `${o.kod}:${o.metin}:${sira}`}
                        className="flex items-start gap-3"
                      >
                        <Rozet ton={ONCELIK_TONU[o.oncelik]}>{ONCELIK_ADI[o.oncelik]}</Rozet>
                        <span className="text-notr-800 text-sm">{o.metin}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Kart>
            </>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Tek ölçüm.
 *
 * ================================================================
 * "KARŞILAŞTIRILAMAZ" ARTIK SESSİZ DEĞİL
 * ================================================================
 * Önceki hâli rozeti HİÇ çizmiyordu; gerekçe "ölçüm yokluğunu bir
 * sonuç gibi göstermemek"ti ve niyeti doğruydu. Ama sonucu şuydu:
 * karşılaştırması ölçülemeyen bir ölçüm, karşılaştırması HİÇ
 * TANIMLANMAMIŞ bir ölçümle (örnek: erteleme oranı) ekranda birebir
 * aynı görünüyordu. Uzman "geçen haftayla aynı mı, yoksa geçen hafta
 * belli değil mi" sorusunu ekrandan cevaplayamıyordu.
 *
 * Sessizlik de bir iddiadır. Rozet yerine sönük bir metin: ölçümün
 * kendisiyle karıştırılmıyor ama yokluğu söyleniyor.
 */
function Olcum({
  etiket,
  deger,
  aciklama,
  degisim,
  farkBicimi,
}: {
  etiket: string;
  deger: string;
  aciklama?: string;
  degisim?: Degisim;
  /** Metriğe göre değişir — oranda "puan", sürede "dk". */
  farkBicimi?: (fark: number) => string;
}) {
  const buyukluk =
    degisim && degisim.fark !== null && degisim.fark !== 0 && farkBicimi
      ? farkBicimi(degisim.fark)
      : null;

  return (
    <div className="bg-notr-0 border-mor-kenar rounded-[1rem] border p-4 shadow-(--golge-unk-kart)">
      <dt className="text-metin-ikincil text-sm">{etiket}</dt>
      <dd className="mt-1 flex flex-wrap items-baseline gap-2">
        <span className="text-metin-koyu text-xl font-bold tabular-nums">{deger}</span>
        {degisim?.yorum === "KARSILASTIRILAMAZ" ? (
          <span className="text-notr-600 text-xs">{YORUM_ADI.KARSILASTIRILAMAZ}</span>
        ) : degisim ? (
          <Rozet ton={YORUM_TONU[degisim.yorum]}>
            {YORUM_ADI[degisim.yorum]}
            {buyukluk ? ` ${buyukluk}` : ""}
          </Rozet>
        ) : null}
      </dd>
      {aciklama && <p className="text-metin-ikincil mt-1 text-xs">{aciklama}</p>}
    </div>
  );
}
